import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOMeta from "@/components/SEOMeta";
import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/Breadcrumbs";
import { 
  Hammer, 
  Wrench,
  Paintbrush,
  Zap,
  Droplet,
  Home,
  TreePine,
  Wind,
  Flame,
  HardHat,
  Truck,
  Building2,
  Drill,
  Warehouse,
  Globe,
  PanelTop,
  DoorClosed,
  Fence,
  Phone
} from "lucide-react";

const servicesData = [
  {
    category: "General Contracting",
    services: [
      { name: "General Contracting", path: "/services/general-contracting", icon: Building2, description: "End-to-end project management for residential and commercial construction" },
      { name: "Skilled Trades", path: "/services/skilled-trades", icon: HardHat, description: "Certified tradespeople for all construction needs" },
      { name: "Specialty Contracting", path: "/services/specialty-contracting", icon: Warehouse, description: "Specialized construction services for unique projects" },
    ]
  },
  {
    category: "Renovations & Remodeling",
    services: [
      { name: "Kitchen Renovations", path: "/services/kitchen-renovations", icon: Home, description: "Custom kitchen remodeling and upgrades" },
      { name: "Bathroom Renovations", path: "/services/bathroom-renovations", icon: Home, description: "Complete bathroom renovation services" },
      { name: "Home Renovations", path: "/services/renovations", icon: Home, description: "Full home renovation and remodeling" },
      { name: "Restoration Services", path: "/services/restoration", icon: Wrench, description: "Property restoration and repair services" },
    ]
  },
  {
    category: "Electrical & Mechanical",
    services: [
      { name: "Electrician Services", path: "/services/electrician", icon: Zap, description: "Licensed electricians for all electrical work" },
      { name: "HVAC Services", path: "/services/hvac", icon: Wind, description: "Heating, ventilation, and air conditioning services" },
      { name: "Plumbing Services", path: "/services/plumber", icon: Droplet, description: "Professional plumbing installation and repair" },
    ]
  },
  {
    category: "Construction & Structural",
    services: [
      { name: "Framing Services", path: "/services/framing", icon: HardHat, description: "Structural framing for new construction" },
      { name: "Carpentry Services", path: "/services/carpenter", icon: Hammer, description: "Expert carpentry and woodworking" },
      { name: "Masonry Services", path: "/services/masonry", icon: Building2, description: "Brick, stone, and concrete masonry" },
      { name: "Roofing Services", path: "/services/roofing", icon: Home, description: "Roof installation, repair, and replacement" },
      { name: "Welding Services", path: "/services/welding", icon: Flame, description: "Professional welding and metal fabrication" },
    ]
  },
  {
    category: "Finishing Services",
    services: [
      { name: "Drywall Services", path: "/services/drywall", icon: PanelTop, description: "Drywall installation and finishing" },
      { name: "Painting Services", path: "/services/painting", icon: Paintbrush, description: "Interior and exterior painting services" },
      { name: "Flooring Services", path: "/services/flooring", icon: Home, description: "All types of flooring installation" },
      { name: "Tiling Services", path: "/services/tiling", icon: PanelTop, description: "Professional tile installation" },
      { name: "Insulation Services", path: "/services/insulation", icon: Home, description: "Insulation installation for energy efficiency" },
    ]
  },
  {
    category: "Exterior & Site Work",
    services: [
      { name: "Asphalt Paving", path: "/services/asphalt-paving", icon: Truck, description: "Asphalt paving and resurfacing" },
      { name: "Concrete Services", path: "/services/asphalt-paving-concrete", icon: Truck, description: "Concrete installation and finishing" },
      { name: "Deck & Fence Services", path: "/services/deck-fence", icon: Fence, description: "Deck construction and fence installation" },
      { name: "Windows & Doors", path: "/services/windows-doors", icon: DoorClosed, description: "Window and door installation" },
      { name: "Landscaping Services", path: "/services/landscaping", icon: TreePine, description: "Professional landscaping and groundwork" },
      { name: "Excavation Services", path: "/services/excavation", icon: Drill, description: "Site excavation and earthmoving" },
      { name: "Demolition Services", path: "/services/demolition", icon: Warehouse, description: "Safe and efficient demolition services" },
    ]
  },
  {
    category: "Specialized Services",
    services: [
      { name: "Heavy Equipment Services", path: "/services/heavy-equipment", icon: Truck, description: "Heavy equipment operation and rental" },
      { name: "Design & Engineering", path: "/services/design-engineering", icon: Globe, description: "Architectural design and engineering support" },
      { name: "Drone Inspections", path: "/services/drone-inspections", icon: Globe, description: "Advanced drone inspection services" },
    ]
  }
];

const Services = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <SEOMeta 
        title="Construction Services Ontario - Blue Knight Contracting"
        description="Professional construction services across Ontario including general contracting, skilled trades, renovations, and specialty services. Serving Toronto, Ottawa, Mississauga, Hamilton, London, Kitchener-Waterloo."
        keywords="construction services Ontario, general contractor, skilled trades, renovations, electrician, plumber, HVAC, roofing, Toronto construction"
      />
      <Header />
      <Breadcrumbs />
      
      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Professional Construction Services Across Ontario
          </h1>
          <p className="text-xl text-muted-foreground mb-8">
            From general contracting to specialized trades, we deliver excellence in every project. 
            Comprehensive construction solutions for residential and commercial properties throughout Ontario.
          </p>
          <Link to="/contact">
            <Button size="lg" className="text-lg px-8">
              <Phone className="mr-2 h-5 w-5" />
              Request a Free Quote
            </Button>
          </Link>
        </div>
      </section>

      {/* Services by Category */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="space-y-16">
            {servicesData.map((category, idx) => (
              <div key={idx}>
                <h2 className="text-3xl font-bold mb-8">{category.category}</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.services.map((service, serviceIdx) => {
                    const Icon = service.icon;
                    return (
                      <Link key={serviceIdx} to={service.path}>
                        <Card className="h-full transition-all hover:shadow-lg hover:border-primary hover:-translate-y-1">
                          <CardHeader>
                            <div className="flex items-center gap-3 mb-2">
                              <div className="p-2 bg-primary/10 rounded-lg">
                                <Icon className="w-6 h-6 text-primary" />
                              </div>
                              <CardTitle className="text-lg">{service.name}</CardTitle>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm text-muted-foreground">
                              {service.description}
                            </p>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold mb-6 text-center">Serving All of Ontario</h2>
          <p className="text-lg text-muted-foreground mb-8 text-center max-w-3xl mx-auto">
            We proudly serve major cities and communities across Ontario with expert construction services
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { name: "Toronto", path: "/toronto" },
              { name: "Ottawa", path: "/ottawa" },
              { name: "Mississauga", path: "/mississauga" },
              { name: "Hamilton", path: "/hamilton" },
              { name: "London", path: "/london" },
              { name: "Kitchener-Waterloo", path: "/kitchener-waterloo" },
              { name: "Brampton", path: "/brampton" },
              { name: "Vaughan", path: "/vaughan" },
              { name: "Markham", path: "/markham" },
              { name: "Oakville", path: "/oakville" },
              { name: "Burlington", path: "/burlington" },
              { name: "St. Catharines", path: "/st-catharines" },
              { name: "Niagara Falls", path: "/niagara-falls" },
              { name: "Welland", path: "/welland" }
            ].map((city) => (
              <Link key={city.name} to={city.path}>
                <div className="p-4 bg-background rounded-lg shadow-sm hover:shadow-md hover:border-primary border border-transparent transition-all">
                  <p className="font-semibold text-center">{city.name}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold mb-12 text-center">Why Choose Blue Knight Contracting</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <HardHat className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Expert Tradespeople</h3>
              <p className="text-muted-foreground">Certified professionals with years of experience</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Quality Workmanship</h3>
              <p className="text-muted-foreground">Superior quality on every project we undertake</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Reliable Service</h3>
              <p className="text-muted-foreground">On-time delivery and excellent customer support</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-primary text-primary-foreground">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Contact us today for a free consultation and quote. Our expert team is ready to help bring your construction vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+1234567890">
              <Button size="lg" variant="secondary" className="text-lg px-8">
                <Phone className="mr-2 h-5 w-5" />
                Call Now
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="text-lg px-8 bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                Get Free Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
